import type { ZendeskCountryCode } from '@repo/shared-config/constants/zendesk';
import { ZENDESK_ARTICLES_IDS } from '@repo/shared-config/constants/zendesk';

const S3_BASE_URL = 'https://qonto-assets.s3.eu-central-1.amazonaws.com/faq_articles_zendesk';
/**
 * Fetches the article mapping JSON from S3.
 *
 * Returns the entire JSON object mapping country codes to article IDs.
 *
 * @param articleSlug - The article slug
 * @returns Country to article ID mapping
 */
export async function fetchArticleJsonFromS3(
  articleSlug: string
): Promise<Record<ZendeskCountryCode, string | null>> {
  const response = await fetch(`${S3_BASE_URL}/${articleSlug}.json`);

  if (!response.ok) {
    throw new Error(`S3 fetch failed with status ${response.status}`);
  }

  return response.json() as Promise<Record<ZendeskCountryCode, string | null>>;
}

/**
 * Extracts the article URL from a country mapping.
 *
 * @param articleMapping - The S3 JSON data (country - article ID mapping)
 * @param organizationCountry - The Zendesk country code
 * @param helpCenterBaseUrl - The base help center URL
 * @returns string - The full article URL or help center URL as fallback
 */
export function getArticleUrlFromMapping(
  articleMapping: Record<ZendeskCountryCode, string | null>,
  organizationCountry: string,
  helpCenterBaseUrl: string
): string {
  if (articleMapping[organizationCountry as ZendeskCountryCode]) {
    return `${helpCenterBaseUrl}/articles/${articleMapping[organizationCountry as ZendeskCountryCode]}`;
  }

  return helpCenterBaseUrl;
}

/**
 * Gets the article ID from config.
 *
 * @param articleSlug - The article slug
 * @param organizationCountry - The Zendesk country code
 * @returns string | null - The article ID from config, or null if not found
 */
export function getArticleIdFromConfig(
  articleSlug: string,
  organizationCountry: string
): string | null {
  return ZENDESK_ARTICLES_IDS[articleSlug]?.[organizationCountry as ZendeskCountryCode]
    ? String(ZENDESK_ARTICLES_IDS[articleSlug][organizationCountry as ZendeskCountryCode])
    : null;
}

export interface GetZendeskArticleUrlParams {
  articleSlug: string;
  helpCenterUrl: string;
  organizationCountry: string;
  s3Data?: Record<ZendeskCountryCode, string | null> | null;
}

/**
 * Gets Zendesk article URL
 *
 * @param articleSlug - The Zendesk article slug
 * @param helpCenterUrl - The base help center URL (e.g., 'https://help.qonto.com/fr')
 * @param organizationCountry - The Zendesk country code
 * @param s3Data - The S3 JSON data (country - article ID mapping), or null if not available
 * @returns The full article URL
 */
export function getZendeskArticleUrl({
  articleSlug,
  helpCenterUrl,
  organizationCountry,
  s3Data,
}: GetZendeskArticleUrlParams): string {
  let url = helpCenterUrl;

  if (s3Data) {
    url = getArticleUrlFromMapping(s3Data, organizationCountry, helpCenterUrl);
  } else {
    const articleId = getArticleIdFromConfig(articleSlug, organizationCountry);

    if (articleId) {
      url = `${helpCenterUrl}/articles/${articleId}`;
    }
  }

  return url;
}
